import React, { useEffect } from "react";
import EditorialSurvey from './pages/EditorialSurvey';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AuthPage from "./pages/AuthPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import HelpPage from "./pages/HelpPage";
import ProfileSelect from "./pages/ProfileSelect";
import HomePage from "./pages/HomePage";
import ContentDetail from "./pages/ContentDetail";
import VideoPlayer from "./pages/VideoPlayer";
import GamePlayer from "./pages/GamePlayer";
import GamepadController from "./pages/GamepadController";
import SearchPage from "./pages/SearchPage";
import MyListPage from "./pages/MyListPage";
import BrowsePage from "./pages/BrowsePage";
import AdminDashboard from "./pages/AdminDashboard";
import AccountPage from "./pages/AccountPage";
import AccountSettings from "./pages/AccountSettings";
import TvApp, { TvErrorBoundary } from "./tv/TvApp";
import { Toaster } from "sonner";
import ActivatePage from "./pages/ActivatePage";
import LandingPage from "./pages/LandingPage";
import LegalPage from "./pages/LegalPage";
import DataDeletionPage from "./pages/DataDeletionPage";
import SignupFlow from "./pages/SignupFlow";
import VerifyCodePage from "./pages/VerifyCodePage";
import GoContent from "./pages/GoContent";
import ActivityTracker from "./components/ActivityTracker";
import { reportAppLaunch } from "./appTelemetry";

const hasSubAccess = (user) => !!user && (user.role === 'admin' || user.access_override || ['active', 'trialing'].includes(user.subscription_status));

function ProtectedRoute({ children, skipProfile = false, requireSub = true }) {
  const { user, loading, activeProfile } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }}>
        <div className="w-10 h-10 border-[3px] border-[#E9641A]/20 border-t-[#E9641A] rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" state={{ from: location.pathname + location.search }} replace />;
  if (requireSub && !hasSubAccess(user)) return <Navigate to="/signup/plans" replace />;
  if (!skipProfile && !activeProfile) return <ProfileSelect />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }}>
        <div className="w-10 h-10 border-[3px] border-[#E9641A]/20 border-t-[#E9641A] rounded-full animate-spin" />
      </div>
    );
  }
  const from = location.state?.from || '/';
  if (user) return <Navigate to={from} replace />;
  return children;
}

function HomeRoute() {
  const { user, loading, activeProfile } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }}>
        <div className="w-10 h-10 border-[3px] border-[#E9641A]/20 border-t-[#E9641A] rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <LandingPage />;
  if (!hasSubAccess(user)) return <Navigate to="/signup/plans" replace />;
  if (!activeProfile) return <ProfileSelect />;
  return <HomePage />;
}

function AppRoutes() {
  useEffect(() => {
    if (window.location.hostname.startsWith('admin.') && window.location.pathname === '/') {
      window.location.replace('/admin');
    }
  }, []);
  return (
    <>
    <ActivityTracker />
    <Routes>
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/help" element={<ProtectedRoute skipProfile requireSub={false}><HelpPage /></ProtectedRoute>} />
      <Route path="/tv" element={<TvErrorBoundary><TvApp /></TvErrorBoundary>} />
      <Route path="/activate" element={<ProtectedRoute skipProfile requireSub={false}><ActivatePage /></ProtectedRoute>} />
      <Route path="/" element={<HomeRoute />} />
      <Route path="/content/:id" element={<ProtectedRoute><ContentDetail /></ProtectedRoute>} />
      <Route path="/go/:id" element={<GoContent />} />
      <Route path="/watch/:id" element={<ProtectedRoute><VideoPlayer /></ProtectedRoute>} />
      <Route path="/game/:id" element={<ProtectedRoute><GamePlayer /></ProtectedRoute>} />
      <Route path="/manette/:room" element={<GamepadController />} />
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      <Route path="/my-list" element={<ProtectedRoute><MyListPage /></ProtectedRoute>} />
      <Route path="/browse" element={<ProtectedRoute><BrowsePage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute skipProfile requireSub={false}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute skipProfile requireSub={false}><AccountPage /></ProtectedRoute>} />
      <Route path="/avis/:id" element={<ProtectedRoute skipProfile requireSub={false}><EditorialSurvey /></ProtectedRoute>} />
      <Route path="/survey/:id" element={<ProtectedRoute skipProfile requireSub={false}><EditorialSurvey /></ProtectedRoute>} />
      <Route path="/account/settings" element={<ProtectedRoute skipProfile requireSub={false}><AccountSettings /></ProtectedRoute>} />
      <Route path="/signup" element={<SignupFlow />} />
      <Route path="/signup/plans" element={<SignupFlow />} />
      <Route path="/signup/success" element={<SignupFlow />} />
      <Route path="/verify-code" element={<VerifyCodePage />} />
      <Route path="/faq" element={<LegalPage />} />
      <Route path="/cgu" element={<LegalPage />} />
      <Route path="/confidentialite" element={<LegalPage />} />
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/privacy-ko" element={<LegalPage />} />
      <Route path="/suppression-compte" element={<DataDeletionPage />} />
      <Route path="/mentions-legales" element={<LegalPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  useEffect(() => { reportAppLaunch(); }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
